// src/components/AssociationMembers.js
import React, { useEffect, useState, useMemo } from "react";
import { jsPDF } from "jspdf";
import html2canvas from "html2canvas";
import { Search, Filter, Download, X, ChevronDown, ChevronUp, ChevronsUpDown } from 'lucide-react';

const disabilityTypes = [
  "ዓይነ ስውር",
  "መስማት የተሳነው",
  "የአካል / የመንቀሳቀስ እክል",
  "የአዕምሮ እድገት ውስንነት",
  "የአእምሮ ጤና እክል",
  "ሥር የሰደደ ሕመም",
  "የመናገር እክል",
  "ኦቲዝም/የነርቭ ስርዓት እክል",
  "የተለየ",
];

const genders = ["ወንድ", "ሴት"];
const maritalStatuses = ["Single", "Married", "Divorced", "Widowed"];
const educationLevels = [
   "መደበኛ ትምህርት ያሎሰደ",
              "አንደኛ ደረጃ",
              "ሁለተኛ ደረጃ",
              "ዲፕሎማ",
              "የመጀመሪያ ዲግሪ",
              "ማስተርስ",
              "ዶክትሬት",
              "ሌላ",
];
const subcities = [
  "አዲስ ከተማ",
  "አቃቂ ቃሊቲ",
  "አራዳ",
  "ቦሌ",
  "ጉለሌ",
  "ቂርቆስ",
  "ኮልፌ ቀራኒዮ",
  "ለሚ ኩራ",
  "ልደታ",
  "ንፋስ ስልክ ላፍቶ",
  "የካ",
];

const API_BASE_URL = 'https://aaapd.org.et/gallery_backend/api';
// Derive the public path for uploaded attachments (stored in ../self_registered_attachments)
const ATTACH_BASE_URL = API_BASE_URL.replace(/\/api$/, '') + '/self_registered_attachments';

export default function AssociationMembers() {
  // Add these states at the top
  const [imageViewerOpen, setImageViewerOpen] = useState(false);
  const [imageToView, setImageToView] = useState(null);

  // Function to open image viewer
  const openImageViewer = (file) => {
    const url = URL.createObjectURL(file);
    setImageToView({ url, name: file.name });
    setImageViewerOpen(true);
  };

  // Function to close image viewer
  const closeImageViewer = () => {
    setImageViewerOpen(false);
    setImageToView(null);
  };

  const [members, setMembers] = useState([]);
  const [opds, setOpds] = useState([]); // OPDs state (from API)
  const [loading, setLoading] = useState(true);

  const [showDetails, setShowDetails] = useState(false);
  const [detailMember, setDetailMember] = useState(null);
  const [attachmentPreview, setAttachmentPreview] = useState(null);

  const [searchTerm, setSearchTerm] = useState("");
  const [filters, setFilters] = useState({
    subCity: "",
    disabilityType: "",
    gender: "",
    kebele: "",
    educationLevel: "",
    maritalStatus: "",
    supportNeeded: "",
    ageMin: "",
    ageMax: "",
  });

  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;
  const [sortConfig, setSortConfig] = useState({ key: "fullName", direction: "asc" });

  const isImageFile = (filename) => {
    if (!filename) return false;
    const ext = filename.split('.').pop().toLowerCase();
    return ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp', 'svg'].includes(ext);
  };

  useEffect(() => {
    fetchMembers();
    fetchOpds();
  }, []);

  const fetchMembers = async () => {
    setLoading(true);
    try {
      const currentUser = JSON.parse(localStorage.getItem("currentUser"));
      const currentUserEmail = currentUser?.email;
      if (!currentUserEmail) {
        setMembers([]);
        setLoading(false);
        return;
      }

      const params = new URLSearchParams({
        email: currentUserEmail,
        ...(searchTerm && { search: searchTerm }),
        ...(filters.disabilityType && { disabilityType: filters.disabilityType }),
        ...(filters.gender && { gender: filters.gender }),
        ...(filters.educationLevel && { educationLevel: filters.educationLevel }),
        ...(filters.maritalStatus && { maritalStatus: filters.maritalStatus }),
        ...(filters.supportNeeded && { supportNeeded: filters.supportNeeded }),
        ...(filters.ageMin && { ageMin: filters.ageMin }),
        ...(filters.ageMax && { ageMax: filters.ageMax })
      });

      const response = await fetch(`${API_BASE_URL}/getAllMembers.php?${params.toString()}`);
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const data = await response.json();
      setMembers(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("Failed to fetch members:", error);
      setMembers([]);
    }
    setLoading(false);
  };

  const fetchOpds = async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/get_opds.php`);
      const data = await res.json();
      if (data?.status === 'success' && Array.isArray(data.data)) {
        setOpds(data.data);
      } else if (Array.isArray(data)) {
        setOpds(data);
      } else {
        setOpds([]);
      }
    } catch (error) {
      console.error("Failed to fetch OPDs:", error);
      setOpds([]);
    }
  };

  const getAge = (dob) => {
    if (!dob) return "";
    const birthDate = new Date(dob);
    const diffMs = Date.now() - birthDate.getTime();
    const ageDt = new Date(diffMs);
    return Math.abs(ageDt.getUTCFullYear() - 1970);
  };

  const onFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
    setCurrentPage(1);
  };

  const downloadPDF = async () => {
    const content = document.getElementById("detail-modal-content");
    const canvas = await html2canvas(content, { scale: 2 });
    const imgData = canvas.toDataURL("image/png");
  
    const pdf = new jsPDF("p", "mm", "a4");
    const imgWidth = 210;
    const imgHeight = (canvas.height * imgWidth) / canvas.width;
    pdf.addImage(imgData, "PNG", 0, 0, imgWidth, imgHeight);
    pdf.save(`${detailMember.fullName || "Member_Details"}.pdf`);
  };

  // ✅ Map from OPD email -> OPD name (for members that only have opdEmail)
  const emailToOpdName = useMemo(() => {
    const map = {};
    opds.forEach((o) => {
      if (o?.contact_email && o?.name) map[o.contact_email] = o.name;
    });
    return map;
  }, [opds]);

  const filteredMembers = useMemo(() => {
    return members
      .filter((member) => {
        const fullName = `${member.firstName} ${member.middleName || ""} ${member.lastName}`.toLowerCase();
        if (
          searchTerm &&
          !fullName.includes(searchTerm.toLowerCase()) &&
          !((member.idNumber || member.uniqueID || "").toString().toLowerCase().includes(searchTerm.toLowerCase()))
        ) return false;
        // Only show approved members
        if ((member.aaapd_status || "").toString().toLowerCase() !== "approved") return false;
        if (filters.subCity) {
          const memberSubCity = (member.subCity || '').toString();
          if (memberSubCity !== filters.subCity) return false;
        }
        if (filters.disabilityType) {
          const target = filters.disabilityType.toLowerCase();
          const memberTypes = (member.disabilityType || "")
            .split(",")
            .map((s) => s.trim().toLowerCase())
            .filter(Boolean);
          if (!memberTypes.includes(target)) return false;
        }
        if (filters.gender && member.gender !== filters.gender) return false;
        if (filters.kebele && (member.kebele || "").toLowerCase() !== filters.kebele.toLowerCase()) return false;
        if (filters.educationLevel && member.educationLevel !== filters.educationLevel) return false;
        if (filters.maritalStatus && member.maritalStatus !== filters.maritalStatus) return false;
        if (filters.supportNeeded && !member.supportNeeded?.toLowerCase().includes(filters.supportNeeded.toLowerCase())) return false;
        // Prefer explicit age, fallback to DOB
        const ageNum = Number(member.age);
        const age = (!Number.isNaN(ageNum) && ageNum > 0) ? ageNum : getAge(member.dateOfBirth);
        if (filters.ageMin && age < Number(filters.ageMin)) return false;
        if (filters.ageMax && age > Number(filters.ageMax)) return false;
        return true;
      })
      .map((member) => ({
        ...member,
        fullName: `${member.firstName} ${member.middleName ? member.middleName + " " : ""}${member.lastName}`,
        age: (!Number.isNaN(Number(member.age)) && Number(member.age) > 0) ? Number(member.age) : getAge(member.dateOfBirth),
        opdName: member.opd_name || emailToOpdName[member.opdEmail] || "Not Assigned",
      }));
  }, [members, searchTerm, filters, opds, emailToOpdName]);
  
  

  const sortedMembers = useMemo(() => {
    const sorted = [...filteredMembers];
    if (sortConfig.key) {
      sorted.sort((a, b) => {
        const aVal = a[sortConfig.key] ?? "";
        const bVal = b[sortConfig.key] ?? "";
        if (aVal < bVal) return sortConfig.direction === "asc" ? -1 : 1;
        if (aVal > bVal) return sortConfig.direction === "asc" ? 1 : -1;
        return 0;
      });
    }
    return sorted;
  }, [filteredMembers, sortConfig]);

  const paginatedMembers = useMemo(() => {
    const start = (currentPage - 1) * itemsPerPage;
    return sortedMembers.slice(start, start + itemsPerPage);
  }, [sortedMembers, currentPage]);

  const totalPages = Math.ceil(sortedMembers.length / itemsPerPage);

  const requestSort = (key) => {
    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") direction = "desc";
    setSortConfig({ key, direction });
  };

  const handleDetail = (member) => {
    setDetailMember(member);
    setShowDetails(true);
  };

  const closeDetailModal = () => {
    setShowDetails(false);
    setDetailMember(null);
  };

  const exportCSV = () => {
    const headers = [
      "Full Name",
      "Gender",
      "Date of Birth",
      "Age",
      "Phone Number",
      "Alternative Phone Number",
      "Email",
      "Location",
      "Sub City",
      "Woreda",
      "House Number",
      "Specific Location",
      "Disability Type",
      "Disability Other",
      "Disability Description",
      "Education Level",
      "Marital Status",
      "Number of Families",
      "Employment Status",
      "Support Needed",
      "Work Needed",
      "Support Received",
      "ID Type",
      "ID Number",
      "Unique ID",
      "Membership Status",
      "OPD Name",
      "Labour ID",
      "Labour ID Number",
      "Place of Work",
      "Office Name",
      "Type of the Work",
      "Job Responsibilities",
      "Passport Photo URL",
      "Receipt/ID Attachment URL",
      "Created At",
      "Updated At"
    ];

    const toCell = (v) => `"${(v ?? '').toString().replace(/"/g, '""')}"`;

    const rows = sortedMembers.map((m) => {
      const photoUrl = m.photoAttachment ? `${ATTACH_BASE_URL}/${m.photoAttachment}` : "";
      const idAttachUrl = m.idAttachment ? `${ATTACH_BASE_URL}/${m.idAttachment}` : "";
      return [
        m.fullName,
        m.gender,
        m.dateOfBirth,
        m.age,
        m.phoneNumber,
        m.AltPhoneNumber,
        m.email,
        m.location,
        m.subCity,
        m.woreda,
        m.houseNumber,
        m.specificLocation,
        m.disabilityType,
        m.disabilityOther,
        m.disabilityDescription,
        m.educationLevel,
        m.maritalStatus,
        m.numberOfFamilies,
        m.employmentStatus,
        m.supportNeeded,
        m.workNeeded,
        m.supportReceived,
        m.idType,
        m.idNumber,
        m.uniqueID,
        m.aaapd_status,
        m.opdName,
        m.labourID,
        m.labourIDNumber,
        m.workPlace,
        m.officeName,
        m.workType,
        m.jobResponsibility,
        photoUrl,
        idAttachUrl,
        m.createdAt,
        m.updatedAt,
      ];
    });

    const csvString = [headers, ...rows]
      .map((row) => row.map(toCell).join(","))
      .join("\n");

    const BOM = "\uFEFF"; // UTF-8 Byte Order Mark to preserve non-Latin characters in Excel
    const blob = new Blob([BOM, csvString], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `association_members_export_${new Date().toISOString().replace(/[:.]/g, '-')}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };
  

  return (
    <div className="bg-gray-100 min-h-screen p-4 md:p-8">
      {/* Header */}
      <header className="mb-8">
        <h1 className="text-4xl font-bold text-gray-800">Association Members</h1>
        <p className="text-gray-500 mt-1">Browse, filter, and manage all members.</p>
      </header>

      {/* Control Panel */}
      <div className="bg-white px-4 py-3 rounded-xl shadow-sm mb-6">
        <div className="grid grid-cols-1 md:grid-cols-4 lg:grid-cols-5 gap-3 items-end">
          {/* Search Input */}
          <div className="relative md:col-span-2 lg:col-span-2">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="search"
              placeholder="Search by name or ID number..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-9 pr-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-teal-500 transition"
            />
          </div>

          {/* Results Count (like MembersPage) */}
          <div className="hidden md:flex items-center">
            <span className="px-3 py-1 rounded-full bg-teal-50 text-teal-700 font-semibold text-sm">
              {filteredMembers.length} ውጤቶች/result{filteredMembers.length !== 1 ? 's' : ''}
            </span>
          </div>

          {/* Filters */}
          <select
            name="subCity"
            value={filters.subCity}
            onChange={onFilterChange}
            className="w-full border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-teal-500 transition bg-white"
          >
            <option value="">ሁሉም ክፍለ ከተሞች</option>
            {subcities.map((name) => (
              <option key={name} value={name}>{name}</option>
            ))}
          </select>

          <select
            name="disabilityType"
            value={filters.disabilityType}
            onChange={onFilterChange}
            className="w-full border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-teal-500 transition bg-white"
          >
            <option value="">ሁሉም የአካል ጉዳት ዓይነቶች</option>
            {disabilityTypes.map((type) => <option key={type} value={type}>{type}</option>)}
          </select>

          <select
            name="gender"
            value={filters.gender}
            onChange={onFilterChange}
            className="w-full border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-teal-500 transition bg-white"
          >
            <option value="">ሁለቱም ፆታዎች</option>
            {genders.map((gender) => <option key={gender} value={gender}>{gender}</option>)}
          </select>

          {/* Export Button */}
          <div className="flex items-center justify-end">
            <button
              className="inline-flex items-center gap-2 px-3 py-1.5 text-sm bg-teal-600 text-white font-medium rounded-lg hover:bg-teal-700 transition-colors shadow-sm whitespace-nowrap"
              onClick={exportCSV}
            >
              <Download className="w-4 h-4" />
              Export CSV
            </button>
          </div>
        </div>
      </div>

      {/* Members Table & Cards */}
      <div className="bg-white rounded-xl shadow-md overflow-hidden">
        {/* Attachment Lightbox Modal */}
        {attachmentPreview && (
          <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/80">
            <div className="relative max-w-5xl max-h-[90vh] w-auto m-4">
              <button
                type="button"
                className="absolute -top-3 -right-3 bg-white text-gray-700 rounded-full shadow p-2 hover:bg-gray-100"
                onClick={() => setAttachmentPreview(null)}
                aria-label="Close attachment preview"
              >
                <X size={20} />
              </button>
              <img
                src={attachmentPreview}
                alt="Attachment Preview"
                className="max-h-[85vh] max-w-full rounded-lg shadow-2xl"
              />
            </div>
          </div>
        )}
        {loading ? (
          <p className="text-center text-gray-500 py-16">Loading members...</p>
        ) : sortedMembers.length === 0 ? (
          <p className="text-center text-gray-500 py-16">No members found matching your criteria.</p>
        ) : (
          <>
            {/* Table for Medium screens and up */}
            <div className="overflow-x-auto hidden md:block">
              <table className="min-w-full text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="py-3 px-4 text-left font-semibold text-gray-600 uppercase tracking-wider w-12">#</th>
                    {[
                      { key: 'fullName', label: 'Full Name' },
                      { key: 'age', label: 'Age' },
                      { key: 'disabilityType', label: 'Disability' },
                      { key: 'gender', label: 'Gender' },
                      { key: 'uniqueID', label: 'ID Number' },
                      { key: 'aaapd_status', label: 'Membership Status' },
                      { key: 'opdName', label: 'Sub City' },
                    ].map(col => (
                      <th key={col.key} onClick={() => requestSort(col.key)} className="py-3 px-4 text-left font-semibold text-gray-600 uppercase tracking-wider cursor-pointer hover:bg-gray-100">
                        <div className="flex items-center gap-2">
                          {col.label}
                          {sortConfig.key === col.key ? (
                            sortConfig.direction === 'asc' ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />
                          ) : <ChevronsUpDown className="w-4 h-4 text-gray-400" />}
                        </div>
                      </th>
                    ))}
                    <th className="py-3 px-4 text-left font-semibold text-gray-600 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {paginatedMembers.map((member, index) => (
                    <tr key={member.id} className="hover:bg-gray-50 transition-colors">
                      <td className="py-3 px-4 text-gray-700">{index + 1}</td>
                      <td className="py-3 px-4 text-gray-700 font-medium">{member.fullName}</td>
                      <td className="py-3 px-4 text-gray-600">{member.age}</td>
                      <td className="py-3 px-4 text-gray-600">{member.disabilityType}</td>
                      <td className="py-3 px-4 text-gray-600">{member.gender}</td>
                      <td className="py-3 px-4 text-gray-600">{member.uniqueID || '-'}</td>
                      <td className="py-3 px-4">
                        {(() => {
                          const v = (member.aaapd_status || '').toString();
                          const s = v.toLowerCase();
                          const cls = s === 'approved'
                            ? 'bg-green-100 text-green-800'
                            : s === 'rejected'
                            ? 'bg-red-100 text-red-800'
                            : s === 'pending'
                            ? 'bg-yellow-100 text-yellow-800'
                            : 'bg-gray-100 text-gray-800';
                          return (
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${cls}`}>
                              {v || '-'}
                            </span>
                          );
                        })()}
                      </td>
                      <td className="py-3 px-4 text-gray-600">{member.subCity}</td>
                      <td className="py-3 px-4">
                        <button className="px-3 py-1 bg-teal-600 text-white text-xs font-semibold rounded-full hover:bg-teal-700" onClick={() => handleDetail(member)}>Details</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Cards for Small screens */}
            <div className="block md:hidden">
              <div className="divide-y divide-gray-200">
                {paginatedMembers.map((member) => (
                  <div key={member.id} className="p-4" onClick={() => handleDetail(member)}>
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <p className="font-bold text-gray-800">{member.fullName}</p>
                        <p className="text-sm text-gray-600">ID: {member.uniqueID || '-'}</p>
                        <p className="text-sm text-gray-500">{member.opdName}</p>
                      </div>
                      <div className="text-right ml-2">
                        <p className="text-sm text-gray-600">Age: {member.age}</p>
                        <span className={`px-2 py-1 text-xs rounded-full mt-1 inline-block ${member.gender === 'Male' ? 'bg-blue-100 text-blue-800' : 'bg-pink-100 text-pink-800'}`}>
                          {member.gender}
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Pagination */}
            <div className="flex flex-col md:flex-row justify-between items-center p-4 border-t border-gray-200 gap-4">
              <span className="text-sm text-gray-600">Showing {paginatedMembers.length} of {sortedMembers.length} members</span>
              <div className="flex items-center gap-2">
                <button
                  className="px-3 py-1 border rounded-lg bg-white text-gray-700 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed transition"
                  onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                  disabled={currentPage === 1}
                >
                  Prev
                </button>
                <span className="text-sm text-gray-700">Page {currentPage} of {totalPages}</span>
                <button
                  className="px-3 py-1 border rounded-lg bg-white text-gray-700 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed transition"
                  onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
                  disabled={currentPage === totalPages}
                >
                  Next
                </button>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Member Details Modal - match MembersPage style */}
      {showDetails && detailMember && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 transition-opacity duration-300 ease-in-out" aria-modal="true" role="dialog">
          <div className="relative bg-gray-100 rounded-xl shadow-2xl w-full max-w-3xl m-4 max-h-[90vh] flex flex-col animate-fade-in-down">
            <div className="flex items-center justify-between p-5 border-b border-gray-200 rounded-t-xl bg-white">
              <div>
                <h3 id="member-details-title" className="text-xl font-semibold text-gray-800">
                  Member Details
                </h3>
                <div className="mt-2">
                  <span className="text-xs text-gray-600 mr-2 uppercase tracking-wide">ID No: </span>
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-sm font-semibold bg-teal-100 text-teal-800">
                    {detailMember.uniqueID || detailMember.idNumber || '-'}
                  </span>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={downloadPDF}
                  className="px-3 py-1.5 rounded-lg bg-teal-600 text-white text-sm font-semibold hover:bg-teal-700"
                  title="Download as PDF"
                >
                  Download PDF
                </button>
                {detailMember.photoAttachment && (
                  isImageFile(detailMember.photoAttachment) ? (
                    <button
                      type="button"
                      className="group"
                      title="View passport photo"
                      onClick={() => setAttachmentPreview(`${ATTACH_BASE_URL}/${detailMember.photoAttachment}`)}
                    >
                      <img
                        src={`${ATTACH_BASE_URL}/${detailMember.photoAttachment}`}
                        alt="Passport Photo"
                        className="h-24 w-20 rounded object-cover object-top border border-gray-200 shadow-sm group-hover:opacity-90"
                        onError={(e) => { e.currentTarget.style.display = 'none'; }}
                      />
                    </button>
                  ) : (
                    <a
                      href={`${ATTACH_BASE_URL}/${detailMember.photoAttachment}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm text-teal-600 hover:underline"
                      title="View passport photo"
                    >
                      Passport Photo
                    </a>
                  )
                )}
                <button
                  type="button"
                  className="p-2 text-gray-400 bg-transparent rounded-lg hover:bg-gray-200 hover:text-gray-900"
                  onClick={closeDetailModal}
                  aria-label="Close details"
                >
                  <X size={20} />
                </button>
              </div>
            </div>
            <div id="detail-modal-content" className="p-6 flex-grow overflow-y-auto">
              <div className="space-y-6">
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                  {[
                    { label: "Full Name", value: `${detailMember.firstName} ${detailMember.middleName || ""} ${detailMember.lastName}` },
                    { label: "Gender", value: detailMember.gender },
                    { label: "Date of Birth", value: detailMember.dateOfBirth },
                    { label: "Age", value: detailMember.age },
                    { label: "Phone Number", value: detailMember.phoneNumber },
                    { label: "Alternative Phone Number", value: detailMember.AltPhoneNumber },
                    { label: "Email", value: detailMember.email },
                    { label: "Location", value: detailMember.location },
                    { label: "Sub City", value: detailMember.subCity },
                    { label: "Woreda", value: detailMember.woreda },
                    { label: "House Number", value: detailMember.houseNumber },
                    { label: "Specific Location", value: detailMember.specificLocation },
                    { label: "Disability Type", value: detailMember.disabilityType === "Other" ? detailMember.disabilityOther || "Other" : detailMember.disabilityType },
                    { label: "Disability Description", value: detailMember.disabilityDescription },
                    { label: "Education Level", value: detailMember.educationLevel },
                    { label: "Marital Status", value: detailMember.maritalStatus },
                    { label: "Number of Families", value: detailMember.numberOfFamilies },
                    { label: "Employment Status", value: detailMember.employmentStatus },
                    { label: "Member Status", value: detailMember.aaapd_status },
                    { label: "Labour ID", value: detailMember.labourID },
                    { label: "Labour ID Number", value: detailMember.labourIDNumber },
                    { label: "Place of Work", value: detailMember.workPlace },
                    { label: "Name of the Office", value: detailMember.officeName },
                    { label: "Type of the Work", value: detailMember.workType },
                    { label: "Job Responsibilities", value: detailMember.jobResponsibility },
                    { label: "Support Needed", value: detailMember.supportNeeded },
                    { label: "Work Needed", value: detailMember.workNeeded },
                    { label: "Support Received", value: detailMember.supportReceived },
                    { label: "ID Type", value: detailMember.idType },
                    { label: "ID Number", value: detailMember.idNumber },
                  ].map(({ label, value }) => (
                    <div key={label}>
                      <p className="text-sm font-medium text-gray-500">{label}</p>
                      {label === "Member Status" ? (
                        (() => {
                          const v = (value || "").toString();
                          const s = v.toLowerCase();
                          const cls = s === "approved"
                            ? "bg-green-100 text-green-800"
                            : s === "rejected"
                            ? "bg-red-100 text-red-800"
                            : s === "pending"
                            ? "bg-yellow-100 text-yellow-800"
                            : "bg-gray-100 text-gray-800";
                          return (
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${cls}`}>
                              {v || "-"}
                            </span>
                          );
                        })()
                      ) : (
                        <p className="text-base text-gray-800 font-semibold">{value || "-"}</p>
                      )}
                    </div>
                  ))}
                </div>
                {/* ID Attachment Preview */}
                {detailMember.idAttachment && (
                  <div className="mt-4">
                    <p className="text-sm font-medium text-gray-500 mb-2">Receipt Attachment</p>
                    {isImageFile(detailMember.idAttachment) ? (
                      <button
                        type="button"
                        title="Click to view"
                        className="inline-block"
                        onClick={() => setAttachmentPreview(`${ATTACH_BASE_URL}/${detailMember.idAttachment}`)}
                      >
                        <img
                          src={`${ATTACH_BASE_URL}/${detailMember.idAttachment}`}
                          alt="Receipt Attachment"
                          className="max-h-64 rounded-lg shadow"
                        />
                      </button>
                    ) : (
                      <a
                        href={`${ATTACH_BASE_URL}/${detailMember.idAttachment}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-sm text-teal-600 hover:underline"
                      >
                        View ID Attachment
                      </a>
                    )}
                  </div>
                )}
                {/* Passport Photo Preview */}
                {detailMember.photoAttachment && (
                  <div className="mt-4">
                    <p className="text-sm font-medium text-gray-500 mb-2">Passport Photo</p>
                    {isImageFile(detailMember.photoAttachment) ? (
                      <button
                        type="button"
                        className="group"
                        onClick={() => setAttachmentPreview(`${ATTACH_BASE_URL}/${detailMember.photoAttachment}`)}
                        title="Click to enlarge"
                      >
                        <img
                          src={`${ATTACH_BASE_URL}/${detailMember.photoAttachment}`}
                          alt="Passport Photo"
                          className="max-h-64 rounded-lg border border-gray-200 shadow-sm group-hover:opacity-90"
                          onError={(e) => { e.currentTarget.style.display = 'none'; }}
                        />
                      </button>
                    ) : (
                      <a
                        href={`${ATTACH_BASE_URL}/${detailMember.photoAttachment}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-teal-600 hover:underline"
                      >
                        View/Download Photo
                      </a>
                    )}
                  </div>
                )}
              </div>
            </div>
            <div className="flex items-center justify-end p-4 border-t border-gray-200 bg-white rounded-b-xl">
              <button
                type="button"
                onClick={closeDetailModal}
                className="px-6 py-2 rounded-lg bg-gray-200 text-gray-800 hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-400 transition"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Image Viewer Modal */}
      {imageViewerOpen && imageToView && (
        <div className="fixed inset-0 bg-black bg-opacity-80 flex justify-center items-center z-[90] p-4 animate-fadeIn">
          <div className="relative">
            <button
              className="absolute -top-4 -right-4 text-white hover:text-gray-300 bg-gray-800 rounded-full p-1"
              onClick={closeImageViewer}
            >
              <X className="w-6 h-6" />
            </button>
            <img
              src={imageToView.url}
              alt={imageToView.name}
              className="max-h-[85vh] w-auto rounded-lg shadow-2xl"
            />
            <p className="text-center mt-3 text-white font-medium text-sm bg-black bg-opacity-50 rounded-b-lg py-1">{imageToView.name}</p>
          </div>
        </div>
      )}
    </div>
  );
}

const InfoItem = ({ label, value }) => (
  <div>
    <p className="text-xs text-gray-500 uppercase tracking-wider">{label}</p>
    <p className="text-gray-800 font-medium">{value || "-"}</p>
  </div>
);
